import {
  buildForest,
  type NamedHierarchyNode,
  type TreeNode,
} from "@contract-manager/shared";
import type { ReactNode } from "react";

/**
 * The tree both admin screens draw, as an SDS `ds-menu`: SDS has no tree,
 * but its menu gives the rows their hover and selected look, and nesting is
 * only an indent on top of that.
 *
 * Folders and teams differ only in what a row means, so the component takes
 * the nodes and two optional slots — a badge after the name, actions at the
 * end of the row — and leaves every decision about them to the page. `buildForest` does the nesting, which keeps the same
 * ordering and the same orphan handling on both screens.
 */
export function HierarchyTree({
  nodes,
  selectedId,
  onSelect,
  renderBadge,
  renderActions,
  emptyMessage,
  label,
}: {
  nodes: readonly NamedHierarchyNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderBadge?: (node: NamedHierarchyNode) => ReactNode;
  renderActions?: (node: NamedHierarchyNode) => ReactNode;
  emptyMessage: string;
  /** Names the menu for screen readers, e.g. "フォルダ". */
  label: string;
}) {
  const forest = buildForest([...nodes]);

  if (forest.length === 0) {
    return <p className="ds-text --muted">{emptyMessage}</p>;
  }

  return (
    <nav className="ds-menu app-side-menu" aria-label={label}>
      <ul>
        {forest.map((node) => (
          <Branch
            key={node.id}
            node={node}
            depth={0}
            selectedId={selectedId}
            onSelect={onSelect}
            {...(renderBadge === undefined ? {} : { renderBadge })}
            {...(renderActions === undefined ? {} : { renderActions })}
          />
        ))}
      </ul>
    </nav>
  );
}

function Branch({
  node,
  depth,
  selectedId,
  onSelect,
  renderBadge,
  renderActions,
}: {
  node: TreeNode<NamedHierarchyNode>;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderBadge?: (node: NamedHierarchyNode) => ReactNode;
  renderActions?: (node: NamedHierarchyNode) => ReactNode;
}) {
  const selected = node.id === selectedId;

  return (
    <li className="app-menu-row">
      <button
        type="button"
        className={`app-tree-name${selected ? " --selected" : ""}`}
        onClick={() => onSelect(node.id)}
        aria-current={selected ? "true" : undefined}
      >
        {/* The indent sits inside the button so the SDS highlight still
            runs the full width of the pane at every depth. */}
        <span
          className="app-menu-label app-truncate app-grow"
          style={{ paddingLeft: `calc(${depth} * var(--space-medium))` }}
        >
          {node.name}
        </span>
        {renderBadge?.(node)}
      </button>
      {renderActions !== undefined && (
        <span className="app-tree-actions">{renderActions(node)}</span>
      )}
      {node.children.length > 0 && (
        <ul>
          {node.children.map((child) => (
            <Branch
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              {...(renderBadge === undefined ? {} : { renderBadge })}
              {...(renderActions === undefined ? {} : { renderActions })}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
